import { useCallback, useEffect, useRef, useState } from "react";
import { useDataChannel, useLocalParticipant } from "@livekit/components-react";
import { toast } from "sonner";
import type { ChatMessage } from "../api/video.api";
import { playChatPing } from "../lib/sound";

interface IncomingChatEnvelope {
  type?: string;
  message?: ChatMessage;
}

interface UseInCallChatNotificationsOptions {
  /** When true, increment count + play sound + show toast on each new message
   *  from a remote participant. Caller passes `activePanel !== "chat"` so
   *  notifications don't fire while the chat panel is already visible. */
  notifyOnReceive: boolean;
}

interface UseInCallChatNotificationsResult {
  unreadCount: number;
  /** Clear the unread badge — call when the user opens the chat panel. */
  reset: () => void;
}

/**
 * Listens to in-call chat broadcasts on the LiveKit data channel and surfaces
 * lightweight notifications (unread badge + ping + toast preview) when the
 * chat panel is closed. Echoes of the local participant's own messages are
 * skipped via sender_id == localParticipant.identity.
 *
 * Lives at InCallView level so the listener stays subscribed even when the
 * chat side-panel is closed (and `InCallChatContent` is unmounted).
 */
export function useInCallChatNotifications({
  notifyOnReceive,
}: UseInCallChatNotificationsOptions): UseInCallChatNotificationsResult {
  const [unreadCount, setUnreadCount] = useState(0);
  const { localParticipant } = useLocalParticipant();

  const decoderRef = useRef<TextDecoder | null>(null);
  if (!decoderRef.current) decoderRef.current = new TextDecoder();

  // Latest-value refs so the data-channel handler doesn't re-subscribe on
  // every render of the parent.
  const notifyRef = useRef(notifyOnReceive);
  notifyRef.current = notifyOnReceive;
  const localIdentityRef = useRef<string | undefined>(localParticipant?.identity);
  useEffect(() => {
    localIdentityRef.current = localParticipant?.identity;
  }, [localParticipant?.identity]);

  useDataChannel((evt) => {
    let parsed: IncomingChatEnvelope;
    try {
      parsed = JSON.parse(decoderRef.current!.decode(evt.payload)) as IncomingChatEnvelope;
    } catch {
      return;
    }
    if (parsed.type !== "chat" || !parsed.message) return;

    const msg = parsed.message;
    // Skip our own echo — LiveKit re-broadcasts to every participant
    // including the sender.
    if (
      localIdentityRef.current &&
      msg.sender_id &&
      msg.sender_id === localIdentityRef.current
    ) {
      return;
    }
    if (!notifyRef.current) return;

    setUnreadCount((c) => c + 1);
    playChatPing();
    toast(`${msg.sender_name}: ${msg.text}`, { duration: 4000 });
  });

  const reset = useCallback(() => setUnreadCount(0), []);

  return { unreadCount, reset };
}
